'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1], 16) / 255, g: parseInt(result[2], 16) / 255, b: parseInt(result[3], 16) / 255 }
    : { r: 0.5, g: 0.5, b: 0.5 };
}

interface ResultInfo {
  url: string;
  size: number;
  filename: string;
}

export default function AddWatermarkTool() {
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [text, setText] = useState('CONFIDENTIAL');
  const [opacity, setOpacity] = useState(30);
  const [fontSize, setFontSize] = useState(48);
  const [rotation, setRotation] = useState(45);
  const [color, setColor] = useState('#808080');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResultInfo | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const drawPreview = useCallback(() => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;

    // Draw page background
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    // Page border
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, W, H);
    // Fake content lines
    ctx.fillStyle = '#f3f4f6';
    for (let i = 0; i < 8; i++) {
      ctx.fillRect(20, 20 + i * 22, W - 40 - (i % 3) * 30, 10);
    }

    // Draw watermark
    if (text.trim()) {
      ctx.save();
      ctx.translate(W / 2, H / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      const { r, g, b } = hexToRgb(color);
      ctx.globalAlpha = opacity / 100;
      ctx.fillStyle = `rgb(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)})`;
      const scaledSize = Math.max(8, (fontSize / 120) * 40);
      ctx.font = `bold ${scaledSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 0, 0);
      ctx.restore();
    }
  }, [text, opacity, fontSize, rotation, color]);

  useEffect(() => {
    drawPreview();
  }, [drawPreview]);

  const loadPDF = async (f: File) => {
    if (f.type !== 'application/pdf') {
      setError('Please upload a valid PDF file.');
      return;
    }
    setError(null);
    setResult(null);
    setFile(f);
    try {
      const { PDFDocument } = await import('pdf-lib');
      const buf = await f.arrayBuffer();
      const pdf = await PDFDocument.load(buf);
      setPageCount(pdf.getPageCount());
    } catch {
      setPageCount(1);
    }
  };

  const handleWatermark = async () => {
    if (!file || !text.trim()) return;
    setProcessing(true);
    setProgress(0);
    setProgressLabel('Loading PDF…');
    setError(null);
    setResult(null);

    try {
      const { PDFDocument, rgb, degrees } = await import('pdf-lib');
      const buf = await file.arrayBuffer();
      setProgress(20);
      setProgressLabel('Reading pages…');
      const pdf = await PDFDocument.load(buf);
      const pages = pdf.getPages();
      const { r, g, b } = hexToRgb(color);

      setProgressLabel('Applying watermark…');
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const { width, height } = page.getSize();
        const textWidth = text.length * fontSize * 0.55;
        page.drawText(text, {
          x: width / 2 - textWidth / 2,
          y: height / 2,
          size: fontSize,
          color: rgb(r, g, b),
          opacity: opacity / 100,
          rotate: degrees(rotation),
        });
        setProgress(20 + Math.round(((i + 1) / pages.length) * 60));
      }

      setProgressLabel('Saving PDF…');
      setProgress(85);
      const bytes = await pdf.save();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setProgress(100);
      setProgressLabel('Done!');
      setResult({ url, size: bytes.byteLength, filename: `watermarked_${file.name}` });
    } catch (err) {
      console.error(err);
      setError('Failed to add watermark. The PDF may be encrypted or corrupted.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.url;
    a.download = result.filename;
    a.click();
  };

  const handleReset = () => {
    if (result?.url) URL.revokeObjectURL(result.url);
    setFile(null);
    setPageCount(0);
    setResult(null);
    setError(null);
    setProgress(0);
    setProgressLabel('');
  };

  return (
    <div className="space-y-5">
      {/* Upload Zone */}
      {!file ? (
        <div
          className={`upload-zone rounded-2xl p-10 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f) loadPDF(f);
          }}
        >
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">💧</span>
          </div>
          <p className="font-semibold text-text-primary mb-1">Upload PDF</p>
          <p className="text-text-muted text-sm">Drag & drop or click to browse</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) loadPDF(f); }}
          />
        </div>
      ) : (
        <div className="space-y-5">
          {/* File Card */}
          <div className="flex items-center gap-3 bg-surface rounded-xl px-4 py-3 border border-gray-100">
            <div className="w-9 h-10 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-[8px] font-black text-red-500">PDF</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">{file.name}</p>
              <p className="text-xs text-text-muted">{formatBytes(file.size)} · {pageCount} {pageCount === 1 ? 'page' : 'pages'}</p>
            </div>
            <button onClick={handleReset} className="text-text-muted hover:text-red-500 transition-colors text-xs font-medium">Remove</button>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <span className="text-red-500 mt-0.5 flex-shrink-0">⚠️</span>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Result */}
          {result ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <span className="text-2xl">✅</span>
              </div>
              <div>
                <p className="font-semibold text-green-800">Watermark Added!</p>
                <p className="text-sm text-green-700 mt-0.5">Output size: {formatBytes(result.size)}</p>
              </div>
              <div className="flex gap-3 justify-center">
                <button onClick={handleDownload} className="btn-primary px-6 py-2.5 text-sm">
                  ⬇ Download PDF
                </button>
                <button onClick={handleReset} className="px-6 py-2.5 text-sm rounded-xl border border-gray-200 text-text-primary hover:bg-gray-50 transition-colors font-medium">
                  New File
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Two-column layout: options + preview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Options */}
                <div className="space-y-4">
                  <div>
                    <label className="block font-semibold text-text-primary text-sm mb-2">Watermark Text</label>
                    <input
                      type="text"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="CONFIDENTIAL"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between mb-1.5">
                      <label className="text-sm font-semibold text-text-primary">Opacity</label>
                      <span className="text-brand-blue text-sm font-bold">{opacity}%</span>
                    </div>
                    <input
                      type="range" min={5} max={100} step={5} value={opacity}
                      onChange={(e) => setOpacity(Number(e.target.value))}
                      className="w-full h-2 rounded-full appearance-none cursor-pointer"
                      style={{ accentColor: '#2563EB' }}
                    />
                  </div>

                  <div>
                    <div className="flex justify-between mb-1.5">
                      <label className="text-sm font-semibold text-text-primary">Font Size</label>
                      <span className="text-brand-blue text-sm font-bold">{fontSize}px</span>
                    </div>
                    <input
                      type="range" min={12} max={120} step={4} value={fontSize}
                      onChange={(e) => setFontSize(Number(e.target.value))}
                      className="w-full h-2 rounded-full appearance-none cursor-pointer"
                      style={{ accentColor: '#2563EB' }}
                    />
                  </div>

                  <div>
                    <div className="flex justify-between mb-1.5">
                      <label className="text-sm font-semibold text-text-primary">Rotation</label>
                      <span className="text-brand-blue text-sm font-bold">{rotation}°</span>
                    </div>
                    <input
                      type="range" min={0} max={360} step={15} value={rotation}
                      onChange={(e) => setRotation(Number(e.target.value))}
                      className="w-full h-2 rounded-full appearance-none cursor-pointer"
                      style={{ accentColor: '#2563EB' }}
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-text-primary text-sm mb-2">Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
                      />
                      <div className="flex gap-2 flex-wrap">
                        {['#808080', '#000000', '#ef4444', '#3b82f6', '#22c55e', '#f59e0b'].map((c) => (
                          <button
                            key={c}
                            onClick={() => setColor(c)}
                            className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${color === c ? 'border-brand-blue scale-110' : 'border-gray-200'}`}
                            style={{ backgroundColor: c }}
                            title={c}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Preview */}
                <div>
                  <label className="block font-semibold text-text-primary text-sm mb-2">Live Preview</label>
                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-gray-50 flex items-center justify-center p-3">
                    <canvas
                      ref={previewCanvasRef}
                      width={220}
                      height={280}
                      className="rounded shadow-sm"
                      style={{ maxWidth: '100%' }}
                    />
                  </div>
                  <p className="text-xs text-text-muted mt-1.5 text-center">Preview approximation — actual PDF may vary slightly</p>
                </div>
              </div>

              {/* Progress */}
              {processing && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-text-muted">
                    <span>{progressLabel}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-blue rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              <button
                onClick={handleWatermark}
                disabled={!text.trim() || processing}
                className="btn-primary w-full text-base py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Adding Watermark…' : 'Add Watermark & Download'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
